/** Helpers for responsive widget sizing via CSS variables + clamp(). */

export const MOBILE_MAX_WIDTH = '767.98px'

export function normalizeSizeValue(raw?: string | null): string | undefined {
  if (raw === undefined || raw === null || String(raw).trim() === '') return undefined
  const value = String(raw).trim()
  if (/^\d+(\.\d+)?$/.test(value)) return `${value}px`
  return value
}

export function parseNumericSize(value: string): { num: number; unit: string } | null {
  const trimmed = value.trim()
  if (trimmed.includes('clamp(')) return null
  const match = trimmed.match(/^([\d.]+)\s*(px|rem|em|%|vh|vw)?$/i)
  if (!match) return null
  return { num: parseFloat(match[1]), unit: (match[2] || 'px').toLowerCase() }
}

/**
 * Desktop value → fluid clamp() when no explicit mobile override is set.
 */
export function toAutoClamp(desktop: string, minRatio = 0.58): string {
  if (!desktop || desktop.includes('clamp(')) return desktop

  const parsed = parseNumericSize(desktop)
  if (!parsed) return desktop

  const { num, unit } = parsed
  if (unit === '%' || unit === 'vw' || unit === 'vh') return desktop

  if (unit === 'rem' || unit === 'em') {
    const min = Math.max(0.75, Math.round(num * minRatio * 100) / 100)
    const mid = Math.round((num * 0.65 + 0.35) * 100) / 100
    return `clamp(${min}${unit}, ${mid}${unit} + 1.6vw, ${num}${unit})`
  }

  const minPx = Math.max(12, Math.round(num * minRatio))
  const midVw = Math.min(40, Math.max(8, Math.round((num / 10) * 10) / 10))
  return `clamp(${minPx}px, ${midVw}vw, ${Math.round(num)}px)`
}

/**
 * Set a CSS custom property pair: --{name} (desktop/clamp) and optional --{name}-mobile.
 */
export function assignResponsiveCssVar(
  styles: Record<string, string>,
  varName: string,
  desktop?: string,
  mobile?: string,
  autoClamp = true,
): void {
  const d = normalizeSizeValue(desktop)
  const m = normalizeSizeValue(mobile)
  if (d) {
    styles[varName] = autoClamp && !m ? toAutoClamp(d) : d
  }
  if (m) {
    styles[`${varName}-mobile`] = m
  }
}

export function assignResponsivePadding(
  styles: Record<string, string>,
  desktop?: string,
  mobile?: string,
): void {
  assignResponsiveCssVar(styles, '--pb-padding', desktop, mobile, true)
  if (desktop && !mobile) {
    styles.padding = `var(--pb-padding)`
  } else if (desktop || mobile) {
    styles.padding = `var(--pb-padding)`
  }
}
